import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spacing, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { selectOverbookingPassengers } from '../../actions/seatsBus';
import PassengerQuantitySelector from '../molecules/PassengerQuantitySelector';
import { getOversoldAvailability } from '../../utils/overbooking';

/**
 * OverbookingPassengerSelector component.
 * It shows a passenger selector for overbooking scenarios where no seats are available.
 * @param {Object} props - The component props.
 * @param {Object} props.trip - The trip object.
 * @param {string} props.way - The way of the trip (departure/return).
 * @param {Array} props.selectedSeats - The selected seats.
 * @returns {JSX.Element} OverbookingPassengerSelector component.
 */
const OverbookingPassengerSelector = ({ trip, way, selectedSeats }) => {
  const { t } = useTranslation('passengers');
  const dispatch = useDispatch();
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { id: tripId } = trip;
  // Calculate current counter from selectedSeats (no local state needed)
  const oversoldSeats = selectedSeats.filter((seat) => seat.category === 'oversold');
  const currentCount = oversoldSeats.length;

  const maxPassengers = getOversoldAvailability(trip?.passengerTypes);
  // Calculate effective limit (minimum between oversold availability and system limit)
  const systemLimit = features.SEAT_SELECTION_LIMIT;
  const effectiveLimit = Math.min(maxPassengers || 0, systemLimit);

  /**
   * Updates Redux store with new passenger count
   * @param {number} newCount - The new passenger count
   */
  const updatePassengerCount = useCallback(
    (newCount) => {
      if (tripId && newCount >= 0 && newCount <= effectiveLimit) {
        dispatch(selectOverbookingPassengers(tripId, newCount, way));
      }
    },
    [tripId, way, dispatch, effectiveLimit],
  );

  /**
   * Increment the passenger counter.
   */
  const handleIncrement = () => {
    if (currentCount < effectiveLimit) {
      updatePassengerCount(currentCount + 1);
    }
  };

  /**
   * Decrement the passenger counter.
   */
  const handleDecrement = () => {
    if (currentCount > 0) {
      updatePassengerCount(currentCount - 1);
    }
  };

  return (
    <div className="px-4 md:px-0">
      <Spacing vertical alignItems="center" size="S">
        <Text color="primary" weight="bold">
          {t('overbooking.title')}
        </Text>
        <Text size="XS">{t('overbooking.description')}</Text>

        {/* Adult passenger selector */}
        <PassengerQuantitySelector
          type="adult"
          counter={currentCount}
          onClickIncrement={handleIncrement}
          onClickDecrement={handleDecrement}
          isDisabled={false}
          iconSize="L"
        />
      </Spacing>
    </div>
  );
};

OverbookingPassengerSelector.propTypes = {
  trip: PropTypes.shape({
    id: PropTypes.string.isRequired,
    passengerTypes: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        availability: PropTypes.number,
      }),
    ),
  }).isRequired,
  way: PropTypes.string.isRequired,
  selectedSeats: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
    }),
  ).isRequired,
};

export default OverbookingPassengerSelector;
